import {
  flow,
  toPairs,
  map,
  groupBy,
  mapValues,
  flatMap,
  sortBy,
} from 'lodash/fp';

import { CHART_CATEGORIES } from 'constants/chart-categories';
import { INFERRED_TYPE_ENUM } from 'constants/chart-channels';
import {
  getMultiSeriesParentChannel,
  getMultiSeriesValue,
} from 'utils/chart/multi-series-name';
import { getColumnIconsForInferredType } from 'components/grid';

// Table Columns need to be ordered into these 'groups' so we specify the order
export const TABLE_COLUMN_ORDER = ['group', 'dynamicColumns', 'value'];

const columnGeneration = ([key, channel]) => {
  const type = getMultiSeriesParentChannel(key);
  const isNumeric =
    channel.aggregate ||
    key === 'dynamicColumns' ||
    (channel.transformedType || channel.inferredType) ===
      INFERRED_TYPE_ENUM.NUMBER;
  return {
    headerName: channel.title,
    headerTooltip: channel.title,
    headerClass: `grid-header-cell-type--${type} ${
      isNumeric ? 'grid-header-cell-type--numeric' : ''
    }`,
    field: key,
    cellClass: `grid-cell-type--${type}`,
    cellStyle: isNumeric ? { flex: 1, 'flex-direction': 'row-reverse' } : {},
    suppressMovable: true,
    minWidth: 80,
    maxWidth: 500,
    width: 150,
    ...getColumnIconsForInferredType(
      isNumeric ? INFERRED_TYPE_ENUM.NUMBER : channel.inferredType
    ),
  };
};

export default {
  name: 'Data Table',
  displayName: 'Table',
  order: 0,
  flavour: 'discrete',
  category: CHART_CATEGORIES.TEXT,
  iconValue: 'data-table',
  channels: [
    {
      required: true,
      name: 'group',
      displayName: 'Groups',
      channelType: 'category',
      intent: 'category-axis',
      multipleSeries: true,
      // this is to allow series & multiple groups
      unrestrictedSeries: true,
    },
    {
      required: false,
      name: 'value',
      displayName: 'Values',
      channelType: 'aggregation',
      intent: 'value-axis',
      multipleSeries: true,
    },
    {
      required: false,
      name: 'dynamicColumns',
      displayName: 'Dynamic Columns',
      channelType: 'category',
      intent: 'series',
    },
  ],
  specFn: (encodings, meta = {}) => {
    const columnOrders = flow(
      toPairs,
      groupBy(([key]) => getMultiSeriesParentChannel(key)),
      mapValues(sortBy(([key]) => getMultiSeriesValue(key)))
    )(encodings);

    const generatedColumns = flow(
      flatMap(column => columnOrders[column] || []),
      map(columnGeneration)
    )(TABLE_COLUMN_ORDER);

    const result = {
      meta,
      columnDefs: generatedColumns,
      defaultColDef: {
        sortable: true,
        resizable: true,
      },
      gridOptions: {
        pinnedBottomRowData: [],
        deltaColumnMode: true,
      },
    };

    return result;
  },
};
